import React from "react";
import { Button } from "./ui/button";
import { Smartphone, Download, Star, Play } from "lucide-react";

import { mockData } from "../mock";

const AppDownloadSection = () => {
  const { links } = mockData.company;

  return (
    <section className="py-24 px-4 relative">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 via-transparent to-orange-500/10"></div>
      
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                  Baixe o App
                </span> e tenha controle total
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Acompanhe tudo direto do seu celular! Interface intuitiva, 
                notificações em tempo real e todas as funcionalidades na palma da sua mão.
              </p>
            </div>

            {/* App features */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span className="text-gray-300">Rastreamento em tempo real</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-gray-300">Notificações push instantâneas</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span className="text-gray-300">Interface otimizada para mobile</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-gray-300">Funciona offline para consultas</span>
              </div>
            </div>

            {/* Download buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                className="bg-black text-white border border-gray-700 hover:bg-gray-900 transition-all duration-300 flex items-center gap-3"
                asChild
              >
                <a href={links.playstore} target="_blank" rel="noopener noreferrer">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <Play className="w-5 h-5 text-black" />
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-gray-400">Disponível no</div>
                    <div className="font-semibold">Google Play</div>
                  </div>
                </a>
              </Button>
              
              <Button 
                size="lg"
                className="bg-black text-white border border-gray-700 hover:bg-gray-900 transition-all duration-300 flex items-center gap-3"
                asChild
              >
                <a href={links.appstore} target="_blank" rel="noopener noreferrer">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-black" />
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-gray-400">Baixar na</div>
                    <div className="font-semibold">App Store</div>
                  </div>
                </a>
              </Button>
            </div>

            {/* App rating */}
            <div className="flex items-center gap-6 pt-4">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-white font-semibold">4.8</span>
              </div>
              <div className="text-gray-400">
                <span className="font-semibold text-white">1.000+</span> downloads
              </div>
            </div>
          </div>
                    {/* Right side - Phone mockups */}
          <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-end mt-12 lg:mt-0 px-4 xl:px-0">
            
            {/* Main phone - Android (Foreground Right) */}
            <div className="relative">
              <div className="w-64 xl:w-72 h-[24rem] xl:h-[26rem] bg-gradient-to-b from-gray-800 to-gray-900 rounded-3xl p-4 xl:p-5 shadow-2xl shadow-yellow-500/20 border border-gray-700 z-10 mx-auto">
                <div className="w-full h-full bg-black rounded-2xl p-4 flex flex-col">
                  {/* Android status bar */}
                  <div className="flex justify-between items-center text-white text-xs mb-4 shrink-0">
                    <span>9:41</span>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-2 bg-green-500 rounded-sm"></div>
                    </div>
                  </div>
                  
                  {/* App store screenshot simulation */}
                  <div className="space-y-4 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 xl:w-12 xl:h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shrink-0">
                          <Smartphone className="w-5 h-5 xl:w-6 xl:h-6 text-black" />
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-sm leading-tight">Eletron Rastreamento</h3>
                          <p className="text-gray-400 text-xs mt-1">Rastreamento Veicular</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-1 mb-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className="w-3 h-3 xl:w-4 xl:h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      
                      <div className="bg-gray-800 rounded-lg p-3 space-y-2">
                        <div className="h-2 xl:h-3 bg-gray-700 rounded w-full"></div>
                        <div className="h-2 xl:h-3 bg-gray-700 rounded w-3/4"></div>
                        <div className="h-2 xl:h-3 bg-gray-700 rounded w-1/2"></div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-gray-800 rounded aspect-square"></div>
                      <div className="bg-gray-800 rounded aspect-square"></div>
                      <div className="bg-gray-800 rounded aspect-square"></div>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full mt-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-semibold shrink-0 text-sm group"
                    asChild
                  >
                    <a href={links.playstore} target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4 mr-2 animate-bounce group-hover:animate-none" />
                      Instalar
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppDownloadSection;